import { Component, Inject, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ErrorReferenceService, NotificationService, OdinCoreModule } from '@odin/odin-core';
import { Observable } from 'rxjs';
import type { SendOtpResponse } from '../../models/SendOtpResponse';

export interface MfaExpiredDialogData {
  deliveryMethod?: 'SMS' | 'Email' | 'App';
  mfaMethod?: 'SMS_MFA' | 'EMAIL_OTP' | 'TOTP' | 'SOFTWARE_TOKEN_MFA';
  mfaSetting?: 'EMAIL_OTP' | 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA';
  sendTo?: string; // Phone number or email address the code was sent to
  // Callback functions provided by the service
  onSendCode?: () => Observable<SendOtpResponse>;
  onResendCode?: () => Observable<SendOtpResponse>;
  onVerifyCode?: (code: string) => Observable<void>;
}

@Component({
  selector: 'odin-elevated-mfa-expired-dialog',
  templateUrl: './elevated-mfa-expired-dialog.component.html',
  styleUrls: ['./elevated-mfa-expired-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    FormsModule,
    MatButtonModule,
    MatInputModule,
    OdinCoreModule
],
})
export class OdinElevatedMfaExpiredDialogComponent {
  mfaCode: string = '';
  errorMessage: string | null = null;
  isSubmitting: boolean = false;

  /* Send Code Handlers */
  codeSent: boolean = false;
  isSendingCode: boolean = false;

  /* Retry Handlers */
  errorMessageOtp: string | null = null;
  retryInSeconds = 0;
  private retryTimer?: ReturnType<typeof setInterval>;  

  /* Cancelation Handlers */
  isConfirmingCancel: boolean = false;

  private errorReference = inject(ErrorReferenceService);
  constructor(
    private notificationService: NotificationService,
    private dialogRef: MatDialogRef<OdinElevatedMfaExpiredDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MfaExpiredDialogData
  ) {}

  isCodeValid(): boolean {
    return /^\d{6}$/.test(this.mfaCode);
  }

  onMfaChange(value: string) {
    this.mfaCode = value.replace(/\D/g, '');

    if (this.isCodeValid() && !this.isSubmitting) {
      this.onSubmit();
    }
  }

  onSubmit(): void {
    this.isSubmitting = true;
    if (!this.isCodeValid()) {
      this.errorMessage = 'Please enter a valid 6-digit code';
      this.isSubmitting = false;
      return;
    }

    if (!this.data.onVerifyCode) {
      const message = 'Verification service not available';
      this.errorMessage = this.errorReference.formatMessage(message, new Error(message));
      this.isSubmitting = false;
      return;
    }

    this.data.onVerifyCode(this.mfaCode).subscribe({
      next: () => {
        this.dialogRef.close({ action: 'verified', code: this.mfaCode });
      },
      error : (err: unknown) => {
        this.errorMessage = this.errorReference.formatMessage('Verification failed. Please try again.', err);
        this.isSubmitting = false;
      }
    });
  }

  /* Cancelation Handlers */
  onCancel(): void {
    this.isConfirmingCancel = true;
  }

  onConfirmationCancel(): void {
    this.notificationService.SmallDialog(
      'Advanced features are locked until MFA verification is completed.',
      'Close',
      5000
    )    
    this.dialogRef.close({ 
      action: 'cancelled'
    });
  }

  onBackToMfa(): void {
    this.isConfirmingCancel = false;
  }

  /* Send Code Handler */
  onSendCode(): void {
    if (this.isSendingCode || !this.data.onSendCode) return;

    this.isSendingCode = true;
    this.data.onSendCode().subscribe({
      next: (response: SendOtpResponse) => {
        this.data.sendTo = response.SentTo;
        this.codeSent = true;
        this.isSendingCode = false;
      },
      error: (err: unknown) => {
        this.errorMessageOtp = this.errorReference.formatMessage('Failed to send code. Please try again.', err);
        this.isSendingCode = false;
        this.startRetryCooldown(60);
      }
    });
  }

  /* Resend Code Handler */
  onResendCode(): void {
    if (this.retryInSeconds > 0 || !this.data.onResendCode) return;

    this.data.onResendCode().subscribe({
      next: (response: SendOtpResponse) => {
        this.data.sendTo = response.SentTo;
        this.mfaCode = '';
        this.notificationService.SmallDialog('A new MFA code has been sent.', 'Close', 3000);
        this.startRetryCooldown(60);
      },
      error: (err: unknown) => {
        this.errorMessageOtp = this.errorReference.formatMessage('Failed to send new code.', err);
        this.startRetryCooldown(60);
      }
    });
  }

  /* Retry Handlers */
  onRetryMfa(): void {
    if (this.retryInSeconds > 0) return;
    
    if (!this.data.onResendCode) {
      const message = 'Resend service not available.';
      this.errorMessageOtp = this.errorReference.formatMessage(message, new Error(message));
      return;
    }

    this.errorMessageOtp = 'Sending new code...';
    this.data.onResendCode().subscribe({
      next: (response: SendOtpResponse) => {
        this.data.sendTo = response.SentTo;

        // Clear error to show input field
        this.errorMessageOtp = null;
        this.mfaCode = ''; // Clear previous attempt

        this.notificationService.SmallDialog(
          'A new MFA code has been sent.',
          'Close',
          3000
        );
      },
      error: (err: unknown) => {
        this.errorMessageOtp = this.errorReference.formatMessage("Failed to send new code.", err);
        this.startRetryCooldown(60);
      }
    });
  }

  startRetryCooldown(seconds: number = 60): void {
    if (this.retryTimer) {
      clearInterval(this.retryTimer);
    }

    this.retryInSeconds = seconds;

    this.retryTimer = setInterval(() => {
      this.retryInSeconds--;

      if (this.retryInSeconds <= 0) {
        clearInterval(this.retryTimer);
        this.retryTimer = undefined;
      }
    }, 1000);
  }
}
